import type { FeedbackMetadata } from "../types";
import { getConsoleErrors } from "../detection/console";
import { getNetworkErrors } from "../detection/network";

interface PostHogData {
  sessionReplayUrl: string | null;
  sessionId: string | null;
  distinctId: string | null;
}

function getPostHogProjectIdFromRemoteConfig(token: string): number | null {
  const rc = (window as unknown as Record<string, unknown>)._POSTHOG_REMOTE_CONFIG as
    | Record<string, unknown>
    | undefined;
  if (!rc) return null;

  const cfg = rc[token] as Record<string, unknown> | undefined;
  if (!cfg) return null;

  const pid = (cfg.project_id ?? cfg.projectId) as unknown;
  if (typeof pid === "number") return pid;
  if (typeof pid === "string") {
    const parsed = Number(pid);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getPostHogUiHostFromRemoteConfig(token: string): string | null {
  const rc = (window as unknown as Record<string, unknown>)._POSTHOG_REMOTE_CONFIG as
    | Record<string, unknown>
    | undefined;
  if (!rc) return null;

  const cfg = rc[token] as Record<string, unknown> | undefined;
  if (!cfg) return null;

  const uiHost = cfg.ui_host as unknown;
  if (typeof uiHost === "string" && uiHost) {
    try {
      return new URL(uiHost).host;
    } catch {
      return uiHost;
    }
  }

  const apiHost = cfg.api_host as unknown;
  if (typeof apiHost === "string" && apiHost) {
    // posthog-js commonly uses https://<region>.i.posthog.com; UI host is <region>.posthog.com
    const match = apiHost.match(/^https?:\/\/([a-z]{2})\.i\.posthog\.com/i);
    if (match) return `${match[1].toLowerCase()}.posthog.com`;
  }

  return null;
}

/**
 * Get PostHog session data. Tries window.posthog first (snippet install),
 * then falls back to reading cookies/localStorage (npm import without global).
 */
function getPostHogData(): PostHogData {
  const empty: PostHogData = { sessionReplayUrl: null, sessionId: null, distinctId: null };

  // Method 1: window.posthog global (snippet install)
  const ph = window.posthog;
  if (ph && typeof ph.capture === "function") {
    try {
      return {
        sessionReplayUrl: ph.get_session_replay_url?.({ withTimestamp: true }) ?? null,
        sessionId: ph.get_session_id?.() ?? null,
        distinctId: ph.get_distinct_id?.() ?? null,
      };
    } catch {
      // fall through
    }
  }

  // Method 2: Read from cookie/localStorage (npm import without window.posthog)
  // posthog-js stores session data in cookie/localStorage named ph_{token}_posthog
  try {
    const token = findPostHogToken();
    if (!token) return empty;

    const storageKey = `ph_${token}_posthog`;

    // Try cookie first, then localStorage
    let data: Record<string, unknown> | null = null;
    const cookieMatch = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${storageKey}=`));
    if (cookieMatch) {
      try {
        data = JSON.parse(decodeURIComponent(cookieMatch.split("=").slice(1).join("=")));
      } catch {
        // ignore
      }
    }
    if (!data) {
      try {
        data = JSON.parse(localStorage.getItem(storageKey) || "null");
      } catch {
        // ignore
      }
    }

    if (!data) return empty;

    // $sesid is [timestamp, sessionId, timestamp]
    const sesid = data.$sesid as [number, string, number] | undefined;
    const sessionId = sesid?.[1] ?? null;
    const distinctId = (data.distinct_id as string) ?? null;

    // Construct replay URL — try host + project ID for a direct link,
    // fall back to just the session ID (PostHog redirects to correct project)
    const projectId = getPostHogProjectIdFromRemoteConfig(token);
    const uiHost = getPostHogUiHostFromRemoteConfig(token);
    const sessionReplayUrl = sessionId
      ? projectId && uiHost
        ? `https://${uiHost}/project/${projectId}/replay/${sessionId}`
        : `https://${uiHost || "us.posthog.com"}/replay/${sessionId}`
      : null;

    return { sessionReplayUrl, sessionId, distinctId };
  } catch {
    return empty;
  }
}

/**
 * Find the PostHog API token from _POSTHOG_REMOTE_CONFIG or cookie names.
 */
function findPostHogToken(): string | null {
  // Check _POSTHOG_REMOTE_CONFIG (set by posthog-js when initialized)
  const rc = (window as unknown as Record<string, unknown>)._POSTHOG_REMOTE_CONFIG as
    | Record<string, unknown>
    | undefined;
  if (rc) {
    const keys = Object.keys(rc);
    if (keys.length > 0) return keys[0];
  }

  // Scan cookies for ph_{token}_posthog pattern
  const match = document.cookie.match(/ph_(phc_[a-zA-Z0-9]+)_posthog/);
  if (match) return match[1];

  return null;
}

/** Page load timestamp for time-on-page calculation */
const PAGE_LOAD_TIME = Date.now();

/**
 * Collect all debug metadata for the current page state.
 */
export function collectMetadata(): FeedbackMetadata {
  const nav = navigator;
  const perf = performance;

  // PostHog data — try window.posthog first, fall back to cookie/localStorage
  // when posthog-js is used via npm import and not exposed globally
  const phData = getPostHogData();
  const posthogSessionReplayUrl = phData.sessionReplayUrl;
  const posthogSessionId = phData.sessionId;
  const posthogDistinctId = phData.distinctId;

  // Page load time
  let pageLoadMs: number | null = null;
  try {
    const navEntry = perf.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navEntry) {
      pageLoadMs = Math.round(navEntry.loadEventEnd - navEntry.startTime);
    }
  } catch {
    // ignore
  }

  // Memory (Chrome only)
  let memoryUsageMb: number | null = null;
  try {
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (mem) {
      memoryUsageMb = Math.round(mem.usedJSHeapSize / 1024 / 1024);
    }
  } catch {
    // ignore
  }

  return {
    userAgent: nav.userAgent,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: screen.width,
    screenHeight: screen.height,
    devicePixelRatio: window.devicePixelRatio,
    url: location.href,
    referrer: document.referrer,
    title: document.title,
    consoleErrors: getConsoleErrors(),
    networkErrors: getNetworkErrors(),
    posthogSessionReplayUrl,
    posthogSessionId,
    posthogDistinctId,
    pageLoadMs,
    memoryUsageMb,
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeSinceLoadMs: Date.now() - PAGE_LOAD_TIME,
  };
}
